import React, { useEffect, useState } from 'react';

/**
 * Lightweight OpenAPI reference. Loads /openapi.yaml (shipped as a static asset),
 * parses it minimally, and renders endpoints grouped by path with method,
 * summary, and required scope. (Keeps the bundle small vs a full Swagger UI.)
 */
export default function ApiReferencePage() {
  const [spec, setSpec] = useState<any>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    fetch('/openapi.yaml')
      .then((r) => r.text())
      .then((t) => setSpec(parseOpenApi(t)))
      .catch(() => setErr('Could not load the API spec.'));
  }, []);

  return (
    <div className="p-6 max-w-4xl">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">API Reference</h1>
      <p className="text-gray-600 mb-6">Authenticate with <code>Authorization: Bearer sk_live_…</code>. Each endpoint lists its required scope.</p>
      {err && <p className="text-red-600">{err}</p>}
      {!spec && !err && <p className="text-gray-500">Loading…</p>}
      {spec && (
        <div className="space-y-3">
          {spec.endpoints.map((e: any, i: number) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <span className={`text-xs font-bold px-2 py-1 rounded ${methodColor(e.method)}`}>{e.method}</span>
                <code className="text-sm">{spec.basePath}{e.path}</code>
              </div>
              {e.summary && <p className="text-sm text-gray-700 mt-2">{e.summary}</p>}
              {e.description && <p className="text-xs text-gray-500 mt-1">{e.description}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function methodColor(m: string) {
  return { GET: 'bg-blue-100 text-blue-700', POST: 'bg-green-100 text-green-700', PATCH: 'bg-amber-100 text-amber-700', DELETE: 'bg-red-100 text-red-700' }[m] || 'bg-gray-100 text-gray-700';
}

/** Minimal YAML-ish OpenAPI parser: extracts server url, paths, methods, summary, description. */
function parseOpenApi(yaml: string) {
  const lines = yaml.split('\n');
  let basePath = '';
  const endpoints: { method: string; path: string; summary?: string; description?: string }[] = [];
  let inPaths = false;
  let curPath = '';
  let curMethod = '';
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const urlM = line.match(/^\s*-?\s*url:\s*(.+)$/);
    if (urlM && !basePath) basePath = urlM[1].trim().replace(/^https?:\/\/[^/]+/, '');
    if (/^paths:/.test(line)) { inPaths = true; continue; }
    if (!inPaths) continue;
    const pathM = line.match(/^ {2}(\/[^\s:]*):/);
    if (pathM) { curPath = pathM[1]; continue; }
    const methodM = line.match(/^ {4}(get|post|put|patch|delete):/);
    if (methodM) { curMethod = methodM[1].toUpperCase(); endpoints.push({ method: curMethod, path: curPath }); continue; }
    const sumM = line.match(/^ {6}summary:\s*(.+)$/);
    if (sumM && endpoints.length) endpoints[endpoints.length - 1].summary = sumM[1].trim();
    const descM = line.match(/^ {6}description:\s*(.+)$/);
    if (descM && endpoints.length) endpoints[endpoints.length - 1].description = descM[1].replace(/^>-?\s*/, '').trim();
  }
  return { basePath, endpoints };
}
